import React from "react";
import CryptoJS from 'crypto-js';
import { ApiCalls, IAccountingCategoriesData, ICrewData, ICurrencyData, IReceiptData } from "./ApiCalls";
import { ISelectData } from "./content/components/elements/Forms/SelectInput";
import { ReduxUpdater } from "./redux/ReduxUpdater";
import { Calendar, DatesArr } from "vanilla-calendar-pro";
import { GlobalConstants } from "./GlobalConstants";

interface IDialogConfig {
    id?: string;
    dateMax?: Date;
    dateMin?: Date;
    textCancel: string;
    textConfirm: string;
    title: string;
    content: React.JSX.Element | string;
    selectedDates: DatesArr | undefined;
    onConfirm: () => void;
    onCancel?: () => void;
    onClickDate: (c: Calendar, e: MouseEvent) => void;
    onStringUpdate: (newVal: string) => void;
}

export class GlobalVariables {
    private static _currencies: ICurrencyData[] = [];
    static accountingCategories: IAccountingCategoriesData[] = [];
    static allowReceiptChanges = false;
    static coOwners: ICrewData[] = [];
    static fileBlob: Blob | null = null;
    static lastTransactionDate = new Date();
    static manualReceipt: IReceiptData | null = null;
    static dialogConfig: IDialogConfig = {
        textCancel: 'Cancel',
        textConfirm: 'OK',
        title: '?',
        content: '?',
        selectedDates: undefined,
        onConfirm: () => {},
        onClickDate: (c: Calendar, e: MouseEvent) => {},
        onStringUpdate: (newVal: string) => {},
    };
    // previous_months_only = false
    // limit_preceding_receipts_to_last_month_start

    static bPreviousMonthsOnly = false;
    static bLimitPrecedingReceiptsToLastMonthStart = false;

    static get coOwnersArray(): ISelectData[] {
        if (GlobalVariables.coOwners.length < 2) {
            return [];
        }
        return [
            ...GlobalVariables.coOwners.map(o => ({
                label: o.first_name,
                value: o.first_name,
            } as ISelectData)),
            {
                label: '- Shared Costs -',
                value: 'SharedCosts',
            }, {
                label: '- 50:50 Split Berth -',
                value: '5050SplitBerth',
            }
        ];
    }

    static get coOwnersExtendedArray(): ISelectData[] {
        return [
            {
                label: 'Select co-owner...',
                value: '',
            },
            ...this.coOwnersArray
        ];
    }

    static get currentMonth(): number {
        return new Date().getMonth();
    }

    static accountingCategoriesFiltered(desc: string | null, doFilter = true): ISelectData[] {
        if (desc && doFilter && desc.toLowerCase().indexOf('food') !== -1) {
            return this.accountingCategoriesArray.filter(a => a.label.indexOf('F&B') !== -1);
        }
        return this.accountingCategoriesArray;
    }

    static createRange(start: number, end:number, step = 1) {
        return Array.from({ length: (end - start) / step + 1 }, (_, index) => start + index * step);
    }

    static userIdToCC(id: number) {
        if (ReduxUpdater.yachtCrewCards.length === 0) {
            return '';
        }
        const user = ReduxUpdater.yachtCrewCards.find(u => u.id === id);
        return user?.card_name ?? '';
    }

    static get accountingCategoriesByID(): { [id: number]: string } {
        const data: { [id: number]: string } = {};
        this.accountingCategories.forEach(c => data[c.id] = c.name);
        return data;
    }

    static get accountingCategoriesArray(): ISelectData[] {
        return [
            {
                label: 'Select valid category...',
                value: '-1',
                disabled: true,
            } as ISelectData,
            ...this.accountingCategories.map(c => {
                return {
                    label: c.name, //`${c.accounting_category} - ${c.accounting_subcategory}`,
                    value: c.id.toString(),
                } as ISelectData;
            })
        ];
    }

    static get currencies(): ICurrencyData[] {
        return this._currencies;
    }

    static set currencies(newCurrencies: ICurrencyData[]) {
        console.log('set currencies', newCurrencies);
        this._currencies = newCurrencies;
        ReduxUpdater.reloadCurrencies()
    }

    static currencySymbol(code: string | null): string {
        return this._currencies.find(c => c.currency_code === code)?.currency_symbol ?? '€';
    };

    // static get currencySymbols(): { [id: string]: string} {
    //     const symbols: { [id: string]: string} = {};
    //     this._currencies.forEach(c => {
    //         symbols[c.currency_code] = c.currency_symbol;
    //     });
    //     return symbols;
    // };

    static handleDownload(data, fileName: string, fileType: string) {
        const blob = new Blob([data], { type: fileType });
        GlobalVariables.handleDownloadedBlob(blob, fileName, fileType);
    };

    static handleDownloadedBlob(blob: Blob, fileName: string, fileType: string) {
        // 1. Generate a temporary URL for the Blob
        const url = window.URL.createObjectURL(blob);

        // 2. Create a hidden <a> element
        const link = document.createElement('a');
        link.href = url;
        link.download = fileName;

        // 3. Append to DOM, click, and cleanup
        document.body.appendChild(link);
        link.click();
        link.parentNode?.removeChild(link);
        window.URL.revokeObjectURL(url);
    };

    static async handleLogin(emailOrPhone: string, pass: string, OnSuccess: () => void, OnFail: (message: string) => void) {
        console.log("logging in");

        ApiCalls.getUser(emailOrPhone).then(user => {
            console.log('ApiCalls.getUser:', user);
            if (user === null || user.password === null || user.password !== CryptoJS.SHA256(pass).toString(CryptoJS.enc.Hex)) {
                return OnFail('Invalid user or password!');
            }
            ReduxUpdater.authenticatedUser = {...user};
            ReduxUpdater.selectedUser = ReduxUpdater.authenticatedUser;
            ReduxUpdater.selectedNav = 0;

            if (user.yacht_id && user.yacht_id > 0) {
                ApiCalls.yacht(user.yacht_id).then(yacht => {
                    ReduxUpdater.currentYacht = yacht;
                    ReduxUpdater.yachtCrew = [{...user}];

                    if ([GlobalConstants.UserRole.STANDARD, GlobalConstants.UserRole.BASIC].includes(user.user_role)) {
                        OnSuccess();
                        return;
                    }

                    ApiCalls.crew(user.yacht_id).then(crew => {
                        console.log('CrewMembers:', crew);
                        if (crew.items.length === 0) {
                            ReduxUpdater.yachtCrew = [];
                            return;
                        }
                        ReduxUpdater.yachtCrew = crew.items;

                        // ApiCalls.cards(user.yacht_id).then(crew => {
                        //     console.log('CrewCards:', crew);
                        //     if (crew.length === 0) {
                        //         ReduxUpdater.yachtCrewCards = [];
                        //         return;
                        //     }
                        //     ReduxUpdater.yachtCrewCards = crew;
                        //     OnSuccess();
                        // }).catch(err => OnFail(err.message));
                        OnSuccess();

                    }).catch(err => OnFail(err.message));
                    
                }).catch(err => OnFail(err.message));
            }
        }).catch(err => OnFail(err.message));
    }

    static handleLogout(OnSuccess: () => void, OnFail: (message: string) => void) {
        console.log("logging out");

        ReduxUpdater.authenticatedUser = null;
        ReduxUpdater.currentYacht = null;
        ReduxUpdater.selectedUser = null;
        ReduxUpdater.yachtCrew = [];
        // ReduxUpdater.yachtCrewCards = [];
        ReduxUpdater.selectedNav = 0;
        ReduxUpdater.selectedNavPos = 0;
        OnSuccess();
    }

    static userHasCard(user: ICrewData | null) {
        if (!user || !user.card_name) {
            return false;
        }
        return user.card_name.toLowerCase() !== 'kx' && user.debit_card_number !== null;
    }
}
